// Bare specifier (not node:crypto): this package is bundled into the web client via
// the barrel, and webpack rejects the node: scheme; the bare form resolves to the
// same core module on the server and to fallback.crypto=false on the client.
import { createHash } from 'crypto';
import {
  GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
  METRIC_REGISTRY_GLOBAL_TENANT_ID,
  METRIC_REGISTRY_SCHEMA_VERSION,
  type DimensionCatalogEntry,
  type MetricRegistryEntry,
} from './metric-registry';

// THE definition-digest authority. The lifecycle stamps rows with these digests at
// promote time, the runtime refuses digest-mismatched ACTIVE rows at boot, and the
// activation-reconciliation receipt predicts that refusal from a plain node script.
// All three MUST share one algorithm, which is why it lives here in packages/lib
// (importable from apps/api and from scripts/ via the built dist) rather than in
// any single service.

export function computeGovernedMetricDefinitionDigest(
  entry: MetricRegistryEntry,
): string {
  const normalized = normalizedMetricDefinition(entry);
  return createHash('sha256').update(stableStringify(normalized)).digest('hex');
}

export function computeGovernedDimensionCatalogDigest(
  entry: DimensionCatalogEntry,
): string {
  return createHash('sha256')
    .update(stableStringify(normalizedDimensionDefinition(entry)))
    .digest('hex');
}

export function governedMetricSignConvention(entry: MetricRegistryEntry) {
  return entry.signConvention ?? { kind: entry.direction, costLine: false };
}

export function governedMetricDimensionsSupported(
  entry: MetricRegistryEntry,
): readonly string[] {
  return sortedUnique(
    entry.dimensionsSupported ?? [
      ...entry.appliesTo.map((scope) => scope.dimension),
      ...entry.aggregationRules.map((rule) => rule.dimension),
    ],
  );
}

function metricDigestIdentity(entry: MetricRegistryEntry) {
  return {
    tenantId: entry.tenantId ?? METRIC_REGISTRY_GLOBAL_TENANT_ID,
    registryLayer: entry.registryLayer,
    industryProfileId:
      entry.industryProfileId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
    companyMappingId:
      entry.companyMappingId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
    metricKey: entry.metricKey,
    registryVersion: entry.registryVersion ?? METRIC_REGISTRY_SCHEMA_VERSION,
  };
}

function dimensionDigestIdentity(entry: DimensionCatalogEntry) {
  return {
    tenantId: entry.tenantId ?? METRIC_REGISTRY_GLOBAL_TENANT_ID,
    registryLayer: entry.registryLayer ?? 'core',
    industryProfileId:
      entry.industryProfileId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
    companyMappingId:
      entry.companyMappingId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
    dimension: entry.dimension,
    registryVersion: entry.registryVersion ?? METRIC_REGISTRY_SCHEMA_VERSION,
  };
}

function normalizedMetricDefinition(entry: MetricRegistryEntry) {
  return {
    ...metricDigestIdentity(entry),
    aliases: sortedUnique(entry.aliases ?? []),
    label: entry.label,
    definition: entry.definition,
    unit: entry.unit,
    frame: entry.frame,
    direction: entry.direction,
    temporalType: entry.temporalType,
    appliesTo: [...entry.appliesTo]
      .map((scope) => ({
        dimension: scope.dimension,
        members: scope.members === 'all' ? 'all' : sortedUnique(scope.members),
      }))
      .sort((left, right) => left.dimension.localeCompare(right.dimension)),
    blendPolicy: entry.blendPolicy,
    aggregationRules: [...entry.aggregationRules].sort((left, right) =>
      `${left.dimension}:${left.rollup}`.localeCompare(
        `${right.dimension}:${right.rollup}`,
      ),
    ),
    reconciliationRule: entry.reconciliationRule,
    requiredEvidenceClasses: sortedUnique(entry.requiredEvidenceClasses),
    benchmarkable: entry.benchmarkable,
    components: entry.components
      ? {
          numerator: [...entry.components.numerator].sort(componentSort),
          denominator: [...entry.components.denominator].sort(componentSort),
        }
      : null,
    cohortQualifier: entry.cohortQualifier ?? null,
    grainSupported: sortedUnique(entry.grainSupported ?? []),
    dimensionsSupported: governedMetricDimensionsSupported(entry),
    signConvention: governedMetricSignConvention(entry),
    sourceOfTruthClass: entry.sourceOfTruthClass ?? 'unclassified',
    // Preserve every legacy digest byte-for-byte: fact scope changes identity
    // only for definitions that explicitly declare immutable GL facts.
    ...(entry.factScope ? { factScope: entry.factScope } : {}),
    composedFrom: entry.composedFrom
      ? {
          ...entry.composedFrom,
          inputMetricKeys: sortedUnique(entry.composedFrom.inputMetricKeys),
        }
      : null,
    targetMetricRef: entry.targetMetricRef ?? null,
    // #5549 — the target-comparison anchor is governed DEFINITION data, so it
    // belongs in the digest. It is spread CONDITIONALLY, not defaulted to null,
    // because an unconditional key would re-digest all 200+ compiled
    // definitions and force a whole-estate re-activation; only the four
    // definitions that actually declare the relation change digest.
    ...(entry.targetComparisonAnchor
      ? { targetComparisonAnchor: entry.targetComparisonAnchor }
      : {}),
    ownerModuleId: entry.ownerModuleId ?? null,
    neverSumWith: sortedUnique(entry.neverSumWith ?? []),
    sourceBindingKeys: sortedUnique(entry.sourceBindingKeys ?? [entry.source]),
    source: entry.source,
  };
}

function normalizedDimensionDefinition(entry: DimensionCatalogEntry) {
  return {
    ...dimensionDigestIdentity(entry),
    label: entry.label,
    physicalColumn: entry.physicalColumn,
    rollups: [...entry.rollups].sort((left, right) =>
      left.rollup.localeCompare(right.rollup),
    ),
    neverBlend: entry.neverBlend
      ? {
          appliesTo: [...entry.neverBlend.appliesTo]
            .map((scope) => ({
              dimension: scope.dimension,
              members:
                scope.members === 'all'
                  ? 'all'
                  : sortedUnique(scope.members),
            }))
            .sort((left, right) =>
              left.dimension.localeCompare(right.dimension),
            ),
        }
      : null,
    source: entry.source,
  };
}

function componentSort(
  left: Readonly<{ metricKey: string }>,
  right: Readonly<{ metricKey: string }>,
): number {
  return left.metricKey.localeCompare(right.metricKey);
}

function sortedUnique(values: readonly string[]): readonly string[] {
  return [...new Set(values)].sort();
}

function stableStringify(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        result[key] = canonicalize(record[key]);
        return result;
      }, {});
  }
  return value;
}
